// EXPERIMENT: psf_featured_spiking_petition_experiment — remove with experiment cleanup
import { useEffect, useEffectEvent } from 'react';

import qs from 'qs';

import { getDocument } from '@change-corgi/core/window';
import { useNavigate } from '@change-corgi/core-react/router';
import { useTracking } from '@change-corgi/core-react/tracking';

import { usePsfPromoteOrSharePageData } from '#src/app/pages/psf/promoteOrShare/pageContext';
import { usePromoteOrShareActionUrls } from '#src/app/pages/psf/promoteOrShare/shared/hooks';
import { useCountryCode } from '#src/app/shared/hooks/l10n';

import type { FeaturedSpikingPetitionArm } from '../useFeaturedSpikingPetitionExperiment';
import { claimFeaturedTrigger } from '../useFeaturedSpikingPetitionExperiment/featuredTriggers';

/** The `selectedChannel` of a `shareChannelSelectedRedirect` event, read without a type assertion */
function getSelectedChannel(event: Event): string | undefined {
	if (!(event instanceof CustomEvent)) return undefined;
	const detail: unknown = event.detail;
	if (typeof detail !== 'object' || detail === null || !('selectedChannel' in detail)) return undefined;
	return typeof detail.selectedChannel === 'string' ? detail.selectedChannel : undefined;
}

/** Copied from `useShareActions/share/success/useRedirect.ts`: the share page URL that redirect goes to. */
const generateRedirectUrl = (shareButtonUrl: string, source: string) => {
	const [baseUrl, queryString] = shareButtonUrl.split('?');

	const queryParams = queryString ? Object.fromEntries(new URLSearchParams(queryString)) : {};

	return `${baseUrl}${qs.stringify({ ...queryParams, slide_up_source: source }, { addQueryPrefix: true })}`;
};

/**
 * Copied from `useCancelableNavigate`'s `deferWhileHidden`: a full page load that starts while a share
 * hands off to another app gives a blank page (CHANGE-85435). Returns a function that drops the wait, or
 * `undefined` if it ran at once.
 */
function runWhenVisible(run: () => void): (() => void) | undefined {
	const doc = getDocument();
	if (doc.visibilityState !== 'hidden') {
		run();
		return undefined;
	}

	const onVisibilityChange = () => {
		if (getDocument().visibilityState === 'hidden') return;
		doc.removeEventListener('visibilitychange', onVisibilityChange);
		run();
	};
	doc.addEventListener('visibilitychange', onVisibilityChange);
	return () => doc.removeEventListener('visibilitychange', onVisibilityChange);
}

/**
 * In `v2`, cancels the share slide-up's redirect to the share page and goes to the featured petition page
 * instead, which continues to that share page URL. In `fake_control`, lets the redirect run and fires
 * `featured_psf_step_would_have_seen` once the tab is visible. Every channel that redirects fires the event: share sheet, app
 * handoffs, copy link (after its delay), and QR Save/Print.
 *
 * Either acts on the first share redirect of a page load only (`featuredTriggers.ts`). A listener that
 * cancelled the event before this one (a Niftic test) keeps it: this one does nothing, in both arms.
 */
export function useFeaturedSpikingPetitionShareRedirect(arm: FeaturedSpikingPetitionArm | null): void {
	const {
		data: {
			petition: { slug },
			featuredSpikingPetition,
		},
	} = usePsfPromoteOrSharePageData();
	const {
		data: { shareButtonUrl },
	} = usePromoteOrShareActionUrls();
	const navigate = useNavigate();
	const track = useTracking();
	const countryCode = useCountryCode();
	const activeArm = (arm === 'v2' || arm === 'fake_control') && featuredSpikingPetition ? arm : null;

	// Effect events, so a new `navigate` identity does not re-run the listener effect, whose cleanup would drop
	// a navigation still waiting for the tab to be visible. The redirect is already cancelled then, so the
	// signer would stay on the combo page.
	const goToFeaturedPage = useEffectEvent((channel: string) => {
		const next = generateRedirectUrl(shareButtonUrl, channel);
		navigate(`/p/${encodeURIComponent(slug)}/psf/featured-petition${qs.stringify({ next }, { addQueryPrefix: true })}`);
	});
	const trackWouldHaveSeen = useEffectEvent((channel: string) => {
		if (!featuredSpikingPetition) return;
		// The same properties as the step's events. The combo page adds the host `petition_id`.
		void track('featured_psf_step_would_have_seen', {
			featured_petition_id: `petition_${featuredSpikingPetition.id}`,
			featured_psf_step_variation: 'fake_control',
			user_country_code: countryCode,
			context: 'share_petition',
			featured_psf_share_channel: channel,
		});
	});

	useEffect(() => {
		if (!activeArm) return undefined;

		let cancelPendingNavigation: (() => void) | undefined;
		let cancelPendingTracking: (() => void) | undefined;

		const onRedirect = (event: Event) => {
			if (event.defaultPrevented) return;
			// A featured page navigation is already waiting for the tab: it goes first, so this redirect stops
			if (cancelPendingNavigation) {
				event.preventDefault();
				return;
			}
			const selectedChannel = getSelectedChannel(event);
			if (selectedChannel === undefined || !claimFeaturedTrigger('share')) return;
			if (activeArm === 'fake_control') {
				// Like `v2`'s step view, which needs the signer back on the tab: a signer who never returns counts in
				// neither arm
				cancelPendingTracking = runWhenVisible(() => {
					cancelPendingTracking = undefined;
					trackWouldHaveSeen(selectedChannel);
				});
				return;
			}
			event.preventDefault();
			cancelPendingNavigation = runWhenVisible(() => {
				cancelPendingNavigation = undefined;
				goToFeaturedPage(selectedChannel);
			});
		};

		window.addEventListener('shareChannelSelectedRedirect', onRedirect);
		return () => {
			window.removeEventListener('shareChannelSelectedRedirect', onRedirect);
			cancelPendingNavigation?.();
			cancelPendingTracking?.();
		};
	}, [activeArm]);
}
